import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdEdit, MdSend, MdCancel, MdDelete, MdSchedule } from "react-icons/md";

import {
  useScheduledEmail,
  useScheduledEmailRecipients,
  useSendEmailImmediately,
  useCancelScheduledEmail,
  useDeleteScheduledEmail,
  useScheduleEmail,
} from "api/hooks/useScheduledEmails";
import { ScheduledEmailStatus } from "api/scheduledemails/scheduledemailsApi";
import { routes } from "consts";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { formatDate } from "utils";
import { PaziaModal } from "components/molecules";
import { useToaster } from "utils";
import { PaziaBox, PaziaBreadcrumbs } from "components/atoms";

const STATUS_LABELS = {
  [ScheduledEmailStatus.Draft]: "Szkic",
  [ScheduledEmailStatus.Scheduled]: "Zaplanowane",
  [ScheduledEmailStatus.Sending]: "Wysyłanie",
  [ScheduledEmailStatus.Sent]: "Wysłane",
  [ScheduledEmailStatus.Failed]: "Niepowodzenie",
  [ScheduledEmailStatus.Cancelled]: "Anulowane",
};

const STATUS_COLORS = {
  [ScheduledEmailStatus.Draft]: "text-gray-600 bg-gray-100",
  [ScheduledEmailStatus.Scheduled]: "text-blue-600 bg-blue-100",
  [ScheduledEmailStatus.Sending]: "text-yellow-600 bg-yellow-100",
  [ScheduledEmailStatus.Sent]: "text-green-600 bg-green-100",
  [ScheduledEmailStatus.Failed]: "text-red-600 bg-red-100",
  [ScheduledEmailStatus.Cancelled]: "text-gray-500 bg-gray-100",
};

export const ScheduledEmailViewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToaster();

  const emailId = parseInt(id || "0");

  const { isLoading: emailLoading, scheduledEmail } = useScheduledEmail(emailId);
  const { isLoading: recipientsLoading, recipients } = useScheduledEmailRecipients(emailId);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<Date | null>(null);
  const [scheduleTime, setScheduleTime] = useState<string>("");

  const sendImmediatelyMutation = useSendEmailImmediately();
  const cancelMutation = useCancelScheduledEmail();
  const deleteMutation = useDeleteScheduledEmail();
  const scheduleMutation = useScheduleEmail();

  if (emailLoading) {
    return <PaziaLoader position="center" />;
  }

  if (!scheduledEmail) {
    return (
      <div>
        <PaziaBreadcrumbs
          segments={[{ title: "Emails", path: routes.EMAILS + routes.SCHEDULED_EMAILS }, { title: "Podgląd" }]}
        />
        <PaziaBox>
          <div className="py-12 text-center">
            <p className="text-gray-500">Nie znaleziono emaila o podanym ID.</p>
            <PaziaButton
              type="primary"
              className="mt-4"
              onClick={() => navigate(routes.EMAILS + routes.SCHEDULED_EMAILS)}
            >
              Powrót do listy
            </PaziaButton>
          </div>
        </PaziaBox>
      </div>
    );
  }

  const handleSendImmediately = async () => {
    try {
      await sendImmediatelyMutation.mutateAsync(emailId);
      showSuccess("Email został dodany do kolejki wysyłania");
      // Refresh the page or navigate back
      window.location.reload();
    } catch (error) {
      showError("Wystąpił błąd podczas wysyłania emaila");
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync(emailId);
      showSuccess("Email został anulowany");
      window.location.reload();
    } catch (error) {
      showError("Wystąpił błąd podczas anulowania emaila");
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Czy na pewno chcesz usunąć ten zaplanowany email?")) {
      try {
        await deleteMutation.mutateAsync(emailId);
        showSuccess("Email został usunięty");
        navigate(routes.EMAILS + routes.SCHEDULED_EMAILS);
      } catch (error) {
        showError("Wystąpił błąd podczas usuwania emaila");
      }
    }
  };

  const handleEdit = () => {
    navigate(`${routes.EMAILS + routes.SCHEDULED_EMAILS}${emailId}/edit`);
  };

  const handleSchedule = () => {
    if (!scheduledEmail) return;

    // Set default to current planned delivery time or tomorrow at 9 AM
    const defaultDate = scheduledEmail.plannedDeliveryTime
      ? new Date(scheduledEmail.plannedDeliveryTime)
      : new Date(Date.now() + 24 * 60 * 60 * 1000);

    setScheduleDate(defaultDate);
    setScheduleTime(defaultDate.toTimeString().slice(0, 5));
    setShowScheduleModal(true);
  };

  const handleScheduleConfirm = async () => {
    if (!scheduleDate || !scheduleTime) {
      showError("Proszę wybrać datę i godzinę wysyłki");
      return;
    }

    try {
      const [hours, minutes] = scheduleTime.split(":");
      const deliveryDateTime = new Date(scheduleDate);
      deliveryDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      await scheduleMutation.mutateAsync({
        id: emailId,
        deliveryTime: deliveryDateTime.toISOString(),
      });

      showSuccess("Email został zaplanowany");
      setShowScheduleModal(false);
      setScheduleDate(null);
      setScheduleTime("");
      window.location.reload();
    } catch (error) {
      showError("Wystąpił błąd podczas planowania emaila");
    }
  };

  const handleScheduleCancel = () => {
    setShowScheduleModal(false);
    setScheduleDate(null);
    setScheduleTime("");
  };

  const canSendImmediately = (status: ScheduledEmailStatus) =>
    status === ScheduledEmailStatus.Draft || status === ScheduledEmailStatus.Scheduled;

  const canSchedule = (status: ScheduledEmailStatus) => status === ScheduledEmailStatus.Draft;

  const canCancel = (status: ScheduledEmailStatus) =>
    status === ScheduledEmailStatus.Draft || status === ScheduledEmailStatus.Scheduled;

  const canEdit = (status: ScheduledEmailStatus) => status === ScheduledEmailStatus.Draft;

  const canDelete = (status: ScheduledEmailStatus) =>
    status === ScheduledEmailStatus.Draft ||
    status === ScheduledEmailStatus.Cancelled ||
    status === ScheduledEmailStatus.Failed;

  const isPastDue =
    !!scheduledEmail.plannedDeliveryTime &&
    new Date(scheduledEmail.plannedDeliveryTime) < new Date() &&
    scheduledEmail.status === ScheduledEmailStatus.Scheduled;

  return (
    <div>
      <PaziaBreadcrumbs
        className="mb-2"
        segments={[{ title: "Emails", path: routes.EMAILS + routes.SCHEDULED_EMAILS }, { title: "Podgląd" }]}
      />

      <div className="space-y-6">
        {/* Email Details */}
        <PaziaBox>
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h2 className="mb-2 text-xl font-semibold text-gray-900">{scheduledEmail.title}</h2>
              <div className="flex items-center space-x-4">
                <span className={`rounded-full px-3 py-1 text-sm font-medium ${STATUS_COLORS[scheduledEmail.status]}`}>
                  {STATUS_LABELS[scheduledEmail.status]}
                </span>
                {isPastDue && (
                  <span className="rounded-full bg-orange-100 px-3 py-1 text-sm font-medium text-orange-600">
                    Przeterminowane
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2">
              {canEdit(scheduledEmail.status) && (
                <PaziaButton type="tertiary" onClick={handleEdit}>
                  <span className="flex items-center">
                    <MdEdit className="mr-2" />
                    Edytuj
                  </span>
                </PaziaButton>
              )}

              {canSchedule(scheduledEmail.status) && (
                <PaziaButton type="primary" onClick={handleSchedule} disabled={scheduleMutation.isPending}>
                  <span className="flex items-center">
                    <MdSchedule className="mr-2" />
                    Zaplanuj
                  </span>
                </PaziaButton>
              )}

              {canSendImmediately(scheduledEmail.status) && (
                <PaziaButton
                  type="primary"
                  onClick={handleSendImmediately}
                  disabled={sendImmediatelyMutation.isPending}
                >
                  <span className="flex items-center">
                    <MdSend className="mr-2" />
                    Wyślij teraz
                  </span>
                </PaziaButton>
              )}

              {canCancel(scheduledEmail.status) && (
                <PaziaButton type="secondary" onClick={handleCancel} disabled={cancelMutation.isPending}>
                  <span className="flex items-center">
                    <MdCancel className="mr-2" />
                    Anuluj
                  </span>
                </PaziaButton>
              )}

              {canDelete(scheduledEmail.status) && (
                <PaziaButton type="danger" onClick={handleDelete} disabled={deleteMutation.isPending}>
                  <span className="flex items-center">
                    <MdDelete className="mr-2" />
                    Usuń
                  </span>
                </PaziaButton>
              )}
            </div>
          </div>

          {/* Email Information Grid */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm text-gray-500">Temat</dt>
                  <dd className="text-sm text-gray-900">{scheduledEmail.subject}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Planowana wysyłka</dt>
                  <dd className="text-sm text-gray-900">
                    {scheduledEmail.plannedDeliveryTime ? (
                      formatDate(new Date(scheduledEmail.plannedDeliveryTime), true, true)
                    ) : (
                      <span className="text-orange-500">Nie zaplanowano</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Utworzono</dt>
                  <dd className="text-sm text-gray-900">
                    {formatDate(new Date(scheduledEmail.createdDate), true, true)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Utworzył</dt>
                  <dd className="text-sm text-gray-900">{scheduledEmail.createdByUser.email}</dd>
                </div>
                {scheduledEmail.emailTemplate && (
                  <div>
                    <dt className="text-sm text-gray-500">Szablon emaila</dt>
                    <dd className="text-sm text-gray-900">{scheduledEmail.emailTemplate.name}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm text-gray-500">Łączna liczba odbiorców</dt>
                  <dd className="text-sm text-gray-900">{scheduledEmail.totalRecipients}</dd>
                </div>
                {scheduledEmail.sentCount > 0 && (
                  <div>
                    <dt className="text-sm text-gray-500">Wysłane</dt>
                    <dd className="text-sm font-medium text-green-600">{scheduledEmail.sentCount}</dd>
                  </div>
                )}
                {scheduledEmail.failedCount > 0 && (
                  <div>
                    <dt className="text-sm text-gray-500">Błędy wysyłki</dt>
                    <dd className="text-sm font-medium text-red-600">{scheduledEmail.failedCount}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm text-gray-500">Pozostało do wysłania</dt>
                  <dd className="text-sm text-gray-900">
                    {scheduledEmail.totalRecipients - scheduledEmail.sentCount - scheduledEmail.failedCount}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </PaziaBox>

        {/* Email Content Preview */}
        <PaziaBox>
          <h3 className="mb-4 text-lg font-medium text-gray-900">Podgląd treści emaila</h3>
          <div className="max-h-96 overflow-y-auto rounded-lg border bg-gray-50 p-4">
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: scheduledEmail.htmlBody }} />
          </div>
        </PaziaBox>

        {/* Recipients List */}
        <PaziaBox>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Odbiorcy ({scheduledEmail.totalRecipients})</h3>
            {recipientsLoading && <span className="text-sm text-gray-500">Ładowanie...</span>}
          </div>

          {!recipientsLoading && recipients.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Użytkownik
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Data wysyłki
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {recipients.map((recipient) => (
                    <tr key={recipient.id}>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {recipient.user ? `${recipient.user.firstName} ${recipient.user.surname}` : "-"}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{recipient.emailAddress}</td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-medium ${
                            recipient.status === 1
                              ? "bg-gray-100 text-gray-600"
                              : recipient.status === 2
                                ? "bg-green-100 text-green-600"
                                : recipient.status === 3
                                  ? "bg-red-100 text-red-600"
                                  : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {recipient.status === 1
                            ? "Oczekuje"
                            : recipient.status === 2
                              ? "Wysłany"
                              : recipient.status === 3
                                ? "Błąd"
                                : "Nieznany"}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {recipient.sentTime ? formatDate(new Date(recipient.sentTime), true, true) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!recipientsLoading && recipients.length === 0 && (
            <div className="py-8 text-center">
              <p className="text-gray-500">Brak odbiorców do wyświetlenia.</p>
            </div>
          )}
        </PaziaBox>
      </div>

      <PaziaModal isOpen={showScheduleModal} onCloseRequest={handleScheduleCancel} id="schedule-email-modal">
        <div className="p-6">
          <h3 className="mb-4 text-lg font-semibold">Zaplanuj wysyłkę emaila</h3>
          <div className="mb-4">
            <p className="mb-4 text-sm text-gray-600">
              Wybierz datę i godzinę wysyłki dla emaila: <strong>{scheduledEmail?.title}</strong>
            </p>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Data wysyłki</label>
                <input
                  type="date"
                  value={scheduleDate ? scheduleDate.toISOString().split("T")[0] : ""}
                  onChange={(e) => setScheduleDate(e.target.value ? new Date(e.target.value) : null)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Godzina wysyłki</label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <PaziaButton type="tertiary" onClick={handleScheduleCancel}>
              Anuluj
            </PaziaButton>
            <PaziaButton
              onClick={handleScheduleConfirm}
              disabled={scheduleMutation.isPending || !scheduleDate || !scheduleTime}
            >
              {scheduleMutation.isPending ? "Planowanie..." : "Zaplanuj"}
            </PaziaButton>
          </div>
        </div>
      </PaziaModal>
    </div>
  );
};
